import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { Breadcrumbs, Select, LayerCard, Text, Badge } from '@cloudflare/kumo'
import { House, MapPin, Phone, EnvelopeSimple, NavigationArrow } from '@phosphor-icons/react'
import { useTranslation, type Locale } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { useRoles } from '../auth/useRoles'
import { apiRequest } from '../api/client'
import type { Building, Room } from '@hub-wifi/shared'

interface ProfilePageProps {
  darkMode: boolean
  onToggleDarkMode: () => void
}

interface BuildingWithRooms extends Building {
  rooms: Room[]
}

interface MyBuildingResponse {
  building: BuildingWithRooms | null
}

export function ProfilePage({ darkMode, onToggleDarkMode }: ProfilePageProps) {
  const { user, getAccessTokenSilently } = useAuth0()
  const { t, locale, setLocale } = useTranslation()
  const roles = useRoles()
  const [building, setBuilding] = useState<BuildingWithRooms | null>(null)

  useEffect(() => {
    async function loadBuilding() {
      try {
        const token = await getAccessTokenSilently()
        const res = await apiRequest<MyBuildingResponse>('/api/my/building', { token })
        setBuilding(res.building)
      } catch {
        // silently ignore — building section just won't show
      }
    }
    void loadBuilding()
  }, [getAccessTokenSilently])

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<House size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('profile.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('profile.title')}
        description={t('profile.description')}
      />

      <div className="max-w-lg space-y-6">
        <LayerCard>
          <LayerCard.Primary>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-kumo-subtle block mb-1">
                  {t('profile.name')}
                </label>
                <Text size="sm">{user?.name ?? '\u2014'}</Text>
              </div>
              <div>
                <label className="text-xs font-medium text-kumo-subtle block mb-1">
                  {t('profile.email')}
                </label>
                <Text size="sm">{user?.email ?? '\u2014'}</Text>
              </div>
              <div>
                <label className="text-xs font-medium text-kumo-subtle block mb-1">
                  {t('profile.roles')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {roles.length > 0 ? (
                    roles.map((role) => (
                      <Badge key={role} variant="primary">
                        {role}
                      </Badge>
                    ))
                  ) : (
                    <Text size="sm">{'\u2014'}</Text>
                  )}
                </div>
              </div>
            </div>
          </LayerCard.Primary>
        </LayerCard>

        {building && (
          <LayerCard>
            <LayerCard.Primary>
              <div className="space-y-4">
                <Text variant="heading2">{t('profile.myBuilding')}</Text>
                <div>
                  <label className="text-xs font-medium text-kumo-subtle block mb-1">
                    {building.name}
                  </label>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-kumo-subtle mt-0.5" />
                  <Text size="sm">{building.address}</Text>
                </div>
                {building.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-kumo-subtle" />
                    <a href={`tel:${building.phone}`} className="text-sm hover:underline">
                      {building.phone}
                    </a>
                  </div>
                )}
                {building.email && (
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple size={14} className="text-kumo-subtle" />
                    <a href={`mailto:${building.email}`} className="text-sm hover:underline">
                      {building.email}
                    </a>
                  </div>
                )}
                {building.googleMapsUrl && (
                  <a
                    href={building.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                  >
                    <NavigationArrow size={14} />
                    {t('profile.openInMaps')}
                  </a>
                )}
              </div>
            </LayerCard.Primary>
          </LayerCard>
        )}

        <LayerCard>
          <LayerCard.Primary>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-kumo-subtle block mb-1.5">
                  {t('profile.language')}
                </label>
                <Select
                  className="w-[200px]"
                  value={locale}
                  onValueChange={(v) => {
                    if (v) setLocale(v as Locale)
                  }}
                >
                  <Select.Option value="pt-BR">Portugues (Brasil)</Select.Option>
                  <Select.Option value="en">English</Select.Option>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-kumo-subtle block mb-1.5">
                  {t('profile.appearance')}
                </label>
                <Select
                  className="w-[200px]"
                  value={darkMode ? 'dark' : 'light'}
                  onValueChange={(v) => {
                    if (v === 'dark' && !darkMode) onToggleDarkMode()
                    if (v === 'light' && darkMode) onToggleDarkMode()
                  }}
                >
                  <Select.Option value="light">{t('profile.appearance.light')}</Select.Option>
                  <Select.Option value="dark">{t('profile.appearance.dark')}</Select.Option>
                </Select>
              </div>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      </div>
    </div>
  )
}
